import { useEffect, useMemo, useState } from 'react';
import {
  Archive,
  AlertCircle,
  Ban,
  Check,
  History,
  Loader2,
  Minus,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  TrendingDown,
  X,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

function NumPad({ value, onChange }) {
  function press(key) {
    if (key === 'C') return onChange('');
    if (key === '<') return onChange(prev => (prev.length > 1 ? prev.slice(0, -1) : ''));
    if (value === '0') return onChange(key);
    if (value.length >= 6) return;
    onChange(prev => prev + key);
  }

  return (
    <div className="grid grid-cols-3 gap-2 mt-3">
      {['7', '8', '9', '4', '5', '6', '1', '2', '3', 'C', '0', '<'].map(key => (
        <button
          key={key}
          type="button"
          onClick={() => press(key)}
          className={`h-12 rounded-xl text-lg font-montserrat font-bold transition-all active:scale-95 ${
            key === 'C'
              ? 'bg-red-50 text-red-500 border border-red-200 hover:bg-red-100'
              : key === '<'
              ? 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
              : 'bg-white text-gray-800 border border-gray-200 hover:bg-gray-50 hover:border-brand-primary'
          }`}
        >
          {key}
        </button>
      ))}
    </div>
  );
}

function ProductIdentity({ product, compact = false }) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      {product.primary_image ? (
        <img
          src={product.primary_image}
          alt={product.product_title}
          className={`${compact ? 'w-9 h-9' : 'w-10 h-10'} rounded-lg object-cover bg-gray-100 shrink-0`}
        />
      ) : (
        <div className={`${compact ? 'w-9 h-9' : 'w-10 h-10'} rounded-lg bg-gray-100 flex items-center justify-center shrink-0`}>
          <Archive size={compact ? 14 : 16} className="text-gray-300" />
        </div>
      )}
      <div className="min-w-0">
        <p className="font-montserrat font-semibold text-sm text-gray-800 truncate">
          {product.product_title}
        </p>
        {product.seller_name && (
          <p className="font-montserrat text-xs text-gray-400 truncate">{product.seller_name}</p>
        )}
      </div>
    </div>
  );
}

function EditQuantityModal({ product, onClose, onSaved, userId }) {
  const [tab, setTab] = useState('set');
  const [input, setInput] = useState('');
  const [sign, setSign] = useState('+');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const currentQty = product.fastmax_quantity ?? product.quantity ?? 0;
  const numericInput = parseInt(input || '0', 10);
  const preview = tab === 'set'
    ? numericInput
    : Math.max(0, currentQty + (sign === '+' ? numericInput : -numericInput));

  function handleChange(valOrUpdater) {
    setError('');
    setInput(prev => (typeof valOrUpdater === 'function' ? valOrUpdater(prev) : valOrUpdater));
  }

  async function handleSave() {
    if (!input) {
      setError('Ingresa una cantidad valida.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const quantity = tab === 'adjust'
        ? (sign === '+' ? numericInput : -numericInput)
        : numericInput;

      await axios.post(`${API_URL}/api/inventory/update-quantity`, {
        product_id: product.product_id,
        inventory_id: product.inventory_id,
        quantity,
        mode: tab,
        user_id: userId,
      });

      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al actualizar. Intenta de nuevo.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell onClose={onClose} maxWidth="max-w-sm">
      <div className="flex items-center justify-between px-5 pt-5 pb-2">
        <ProductIdentity product={product} compact />
        <IconButton onClick={onClose} title="Cerrar">
          <X size={18} />
        </IconButton>
      </div>

      <div className="px-5 pb-3">
        <span className="text-xs font-montserrat text-gray-500">
          Stock Fast Max actual: <span className="font-bold text-gray-700">{currentQty} uds</span>
        </span>
      </div>

      <div className="flex mx-5 bg-gray-100 rounded-xl p-1 gap-1">
        {[{ id: 'set', label: 'Establecer' }, { id: 'adjust', label: 'Ajustar' }].map(item => (
          <button
            key={item.id}
            type="button"
            onClick={() => { setTab(item.id); setInput(''); setError(''); }}
            className={`flex-1 py-2 rounded-lg text-sm font-montserrat font-semibold transition-all ${
              tab === item.id
                ? 'bg-white text-brand-primary shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="px-5 pb-5 pt-3 space-y-3">
        {tab === 'adjust' && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSign('+')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl border text-sm font-montserrat font-bold transition-all ${
                sign === '+'
                  ? 'bg-green-50 border-green-300 text-green-700'
                  : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'
              }`}
            >
              <Plus size={15} /> Agregar
            </button>
            <button
              type="button"
              onClick={() => setSign('-')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl border text-sm font-montserrat font-bold transition-all ${
                sign === '-'
                  ? 'bg-red-50 border-red-300 text-red-700'
                  : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'
              }`}
            >
              <Minus size={15} /> Restar
            </button>
          </div>
        )}

        <div className={`flex items-center justify-center rounded-xl border-2 py-3 transition-colors ${
          input ? 'border-brand-primary bg-pink-50/30' : 'border-gray-200 bg-gray-50'
        }`}>
          {tab === 'adjust' && input && (
            <span className={`font-montserrat font-extrabold text-2xl mr-1 ${
              sign === '+' ? 'text-green-600' : 'text-red-500'
            }`}>
              {sign}
            </span>
          )}
          <span className={`font-montserrat font-extrabold text-3xl ${input ? 'text-gray-800' : 'text-gray-300'}`}>
            {input || '0'}
          </span>
          <span className="font-montserrat text-gray-400 text-sm ml-1.5 self-end mb-1">uds</span>
        </div>

        {input && (
          <div className={`text-center text-xs font-montserrat font-semibold rounded-lg py-1.5 ${
            tab === 'set'
              ? 'bg-blue-50 text-blue-700'
              : sign === '+'
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-600'
          }`}>
            {tab === 'set' ? `Nuevo stock: ${preview} uds` : `${currentQty} -> ${preview} uds`}
          </div>
        )}

        <NumPad value={input} onChange={handleChange} />

        {error && <p className="text-xs font-montserrat text-red-500 text-center">{error}</p>}

        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !input}
          className="w-full py-3 rounded-xl bg-brand-primary text-white font-montserrat font-bold text-sm transition-all hover:bg-brand-primary/90 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] mt-1"
        >
          {saving ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 size={15} className="animate-spin" /> Guardando...
            </span>
          ) : 'Guardar'}
        </button>
      </div>
    </ModalShell>
  );
}

function ReceptionReviewModal({ request, userId, onClose, onSaved }) {
  const [acceptedQty, setAcceptedQty] = useState(String(request.pending_quantity || request.requested_quantity || 0));
  const [adminNotes, setAdminNotes] = useState(request.admin_notes || '');
  const [savingAction, setSavingAction] = useState('');
  const [error, setError] = useState('');

  const qty = parseInt(acceptedQty || '0', 10);

  async function submit(status) {
    setSavingAction(status);
    setError('');
    try {
      await axios.patch(`${API_URL}/api/admin/inventory/reception-requests/${request.id}`, {
        status,
        accepted_quantity: status === 'rejected' ? 0 : qty,
        admin_notes: adminNotes.trim(),
        reviewed_by: userId,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo revisar la solicitud.');
      console.error(err);
    } finally {
      setSavingAction('');
    }
  }

  return (
    <ModalShell onClose={onClose} maxWidth="max-w-lg">
      <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
        <div>
          <h3 className="font-montserrat font-extrabold text-lg text-gray-800">Revisar recepcion</h3>
          <p className="font-montserrat text-xs text-gray-400">{request.seller_name || 'Tienda'} - {request.sku}</p>
        </div>
        <IconButton onClick={onClose} title="Cerrar">
          <X size={18} />
        </IconButton>
      </div>

      <div className="p-5 space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <StatBox label="Solicitado" value={request.requested_quantity} />
          <StatBox label="Aceptado" value={request.accepted_quantity} />
          <StatBox label="Pendiente" value={request.pending_quantity} />
        </div>

        <label className="block">
          <span className="block text-xs font-montserrat font-bold text-gray-500 mb-1">Cantidad a aceptar</span>
          <input
            type="number"
            min="0"
            max={request.pending_quantity}
            value={acceptedQty}
            onChange={event => setAcceptedQty(event.target.value)}
            className="w-full px-3 py-2.5 text-sm font-montserrat bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-brand-primary"
          />
        </label>

        <label className="block">
          <span className="block text-xs font-montserrat font-bold text-gray-500 mb-1">Notas del admin</span>
          <textarea
            value={adminNotes}
            onChange={event => setAdminNotes(event.target.value)}
            rows={3}
            className="w-full px-3 py-2.5 text-sm font-montserrat bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-brand-primary resize-none"
            placeholder="Opcional"
          />
        </label>

        {request.notes && (
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-3">
            <p className="text-xs font-montserrat font-bold text-gray-500 mb-1">Notas del usuario</p>
            <p className="text-sm font-montserrat text-gray-700 whitespace-pre-wrap">{request.notes}</p>
          </div>
        )}

        {error && <p className="text-xs font-montserrat text-red-500">{error}</p>}

        <div className="grid grid-cols-3 gap-2">
          <ActionButton
            onClick={() => submit('accepted')}
            disabled={!!savingAction || qty !== request.pending_quantity}
            icon={<Check size={15} />}
            label={savingAction === 'accepted' ? 'Guardando...' : 'Aceptar'}
            tone="green"
          />
          <ActionButton
            onClick={() => submit('partially_accepted')}
            disabled={!!savingAction || qty <= 0 || qty >= request.pending_quantity}
            icon={<Pencil size={15} />}
            label={savingAction === 'partially_accepted' ? 'Guardando...' : 'Parcial'}
            tone="amber"
          />
          <ActionButton
            onClick={() => submit('rejected')}
            disabled={!!savingAction}
            icon={<Ban size={15} />}
            label={savingAction === 'rejected' ? 'Guardando...' : 'Rechazar'}
            tone="red"
          />
        </div>
      </div>
    </ModalShell>
  );
}

function HistoryModal({ product, onClose }) {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function fetchHistory() {
      setLoading(true);
      setError('');
      try {
        const { data } = await axios.get(`${API_URL}/api/admin/products/${product.product_id}/inventory-history`);
        if (!cancelled) setHistory(data.items || []);
      } catch (err) {
        if (!cancelled) setError('No se pudo cargar el historial.');
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchHistory();
    return () => { cancelled = true; };
  }, [product.product_id]);

  return (
    <ModalShell onClose={onClose} maxWidth="max-w-2xl">
      <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
        <div>
          <h3 className="font-montserrat font-extrabold text-lg text-gray-800">Historial</h3>
          <p className="font-montserrat text-xs text-gray-400">{product.product_title} - {product.sku}</p>
        </div>
        <IconButton onClick={onClose} title="Cerrar">
          <X size={18} />
        </IconButton>
      </div>

      <div className="p-5 max-h-[70vh] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12 gap-2 text-gray-400 font-montserrat text-sm">
            <Loader2 size={18} className="animate-spin" /> Cargando historial...
          </div>
        ) : error ? (
          <p className="text-sm font-montserrat text-red-500">{error}</p>
        ) : history.length === 0 ? (
          <p className="text-sm font-montserrat text-gray-400 text-center py-12">Sin movimientos registrados</p>
        ) : (
          <div className="space-y-3">
            {history.map(item => (
              <div key={`${item.type}-${item.id}`} className="border border-gray-100 rounded-xl p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-montserrat text-sm font-bold text-gray-800">
                      {historyTitle(item)}
                    </p>
                    <p className="font-montserrat text-xs text-gray-400 mt-0.5">
                      {formatDate(item.date)}{item.actor_name ? ` - ${item.actor_name}` : ''}
                    </p>
                  </div>
                  {item.status && <StatusBadge status={item.status} />}
                </div>
                {(item.notes || item.admin_notes) && (
                  <div className="mt-2 text-xs font-montserrat text-gray-500 space-y-1">
                    {item.notes && <p>Usuario: {item.notes}</p>}
                    {item.admin_notes && <p>Admin: {item.admin_notes}</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </ModalShell>
  );
}

export function InventoryView({ dashboardData, userId }) {
  const { inventory, inventorySummary, inventoryLoading, inventoryError, fetchInventory } = dashboardData;

  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [reviewRequest, setReviewRequest] = useState(null);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState('');

  useEffect(() => {
    fetchInventory(lowStockOnly);
    fetchReceptionRequests();
  }, [lowStockOnly, fetchInventory]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return inventory;
    return inventory.filter(product => (
      product.product_title?.toLowerCase().includes(term) ||
      product.sku?.toLowerCase().includes(term) ||
      product.seller_name?.toLowerCase().includes(term)
    ));
  }, [inventory, searchTerm]);

  async function fetchReceptionRequests() {
    setRequestsLoading(true);
    setRequestsError('');
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/inventory/reception-requests`, {
        params: { status: 'pending' },
      });
      setRequests(data.items || []);
    } catch (err) {
      setRequestsError('No se pudieron cargar las solicitudes pendientes');
      console.error(err);
    } finally {
      setRequestsLoading(false);
    }
  }

  function refreshAll() {
    fetchInventory(lowStockOnly);
    fetchReceptionRequests();
  }

  function handleSaved() {
    setEditingProduct(null);
    setReviewRequest(null);
    refreshAll();
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-montserrat font-extrabold text-xl text-gray-800">Inventario Fast Max</h2>
            {inventorySummary && (
              <p className="font-montserrat text-sm text-gray-500 mt-0.5">
                {inventorySummary.total_products} productos -{' '}
                <span className={inventorySummary.low_stock_count > 0 ? 'text-amber-600 font-semibold' : ''}>
                  {inventorySummary.low_stock_count} con stock bajo
                </span>
                {inventorySummary.out_of_stock_count > 0 && (
                  <span className="text-red-600 font-semibold">
                    {' '} - {inventorySummary.out_of_stock_count} sin stock
                  </span>
                )}
                {inventorySummary.pending_reception_count > 0 && (
                  <span className="text-blue-600 font-semibold">
                    {' '} - {inventorySummary.pending_reception_count} solicitudes pendientes
                  </span>
                )}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setLowStockOnly(prev => !prev)}
              className={`flex items-center gap-2 text-sm font-montserrat font-semibold px-3 py-2 rounded-lg border transition-colors ${
                lowStockOnly
                  ? 'bg-amber-50 border-amber-300 text-amber-700'
                  : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
            >
              <TrendingDown size={15} />
              Stock bajo
            </button>

            <button
              onClick={refreshAll}
              disabled={inventoryLoading || requestsLoading}
              className="flex items-center gap-2 text-sm font-montserrat font-semibold text-gray-500 hover:text-brand-primary transition-colors disabled:opacity-50 px-3 py-2 rounded-lg border border-gray-200 hover:border-gray-300"
              title="Actualizar"
            >
              <RefreshCw size={15} className={inventoryLoading || requestsLoading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        <PendingRequestsTable
          requests={requests}
          loading={requestsLoading}
          error={requestsError}
          onReview={setReviewRequest}
        />

        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
            placeholder="Buscar producto, SKU o tienda..."
            className="w-full pl-9 pr-8 py-2.5 text-sm font-montserrat bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-brand-primary transition-colors"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              title="Limpiar busqueda"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {inventoryError && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm font-montserrat text-red-600">
            {inventoryError}
          </div>
        )}

        {inventoryLoading ? (
          <div className="flex items-center justify-center py-24 gap-3">
            <Loader2 size={24} className="animate-spin text-brand-primary" />
            <span className="font-montserrat text-gray-400 text-sm">Cargando inventario...</span>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <Archive size={40} className="text-gray-200" />
            <p className="font-montserrat text-gray-400 text-sm">
              {lowStockOnly ? 'Sin productos con stock bajo' : 'Sin productos en inventario'}
            </p>
          </div>
        ) : (
          <InventoryTable
            rows={filteredRows}
            onEdit={setEditingProduct}
            onHistory={setHistoryProduct}
          />
        )}
      </div>

      {editingProduct && (
        <EditQuantityModal
          product={editingProduct}
          onClose={() => setEditingProduct(null)}
          onSaved={handleSaved}
          userId={userId}
        />
      )}

      {reviewRequest && (
        <ReceptionReviewModal
          request={reviewRequest}
          userId={userId}
          onClose={() => setReviewRequest(null)}
          onSaved={handleSaved}
        />
      )}

      {historyProduct && (
        <HistoryModal
          product={historyProduct}
          onClose={() => setHistoryProduct(null)}
        />
      )}
    </div>
  );
}

function PendingRequestsTable({ requests, loading, error, onReview }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div>
          <h3 className="font-montserrat font-bold text-sm text-gray-800">Solicitudes de recepcion pendientes</h3>
          <p className="font-montserrat text-xs text-gray-400">{requests.length} solicitudes por revisar</p>
        </div>
        {loading && <Loader2 size={16} className="animate-spin text-brand-primary" />}
      </div>

      {error ? (
        <div className="px-4 py-3 text-sm font-montserrat text-red-500">{error}</div>
      ) : requests.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm font-montserrat text-gray-400">
          No hay solicitudes pendientes
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1040px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <Th>Tienda</Th>
                <Th>Producto</Th>
                <Th>SKU</Th>
                <Th>Solicitado</Th>
                <Th>Aceptado</Th>
                <Th>Pendiente</Th>
                <Th>Estado</Th>
                <Th>Notas usuario</Th>
                <Th>Notas admin</Th>
                <Th>Fecha</Th>
                <Th>Accion</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {requests.map(req => (
                <tr key={req.id} className="hover:bg-gray-50/60">
                  <Td>{req.seller_name || '-'}</Td>
                  <Td strong>{req.product_title || '-'}</Td>
                  <td className="px-4 py-3">
                    <Sku value={req.sku} />
                  </td>
                  <Td>{req.requested_quantity}</Td>
                  <Td>{req.accepted_quantity}</Td>
                  <Td>{req.pending_quantity}</Td>
                  <td className="px-4 py-3">
                    <StatusBadge status={req.status} />
                  </td>
                  <Td muted>{req.notes || '-'}</Td>
                  <Td muted>{req.admin_notes || '-'}</Td>
                  <Td muted>{formatDate(req.requested_at)}</Td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => onReview(req)}
                      className="px-3 py-2 rounded-lg bg-brand-primary text-white text-xs font-montserrat font-bold hover:bg-brand-primary/90 transition-colors"
                    >
                      Revisar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InventoryTable({ rows, onEdit, onHistory }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[860px]">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <Th>Producto</Th>
              <Th>Tienda</Th>
              <Th>SKU</Th>
              <Th>Precio</Th>
              <Th>Stock Fast Max</Th>
              <Th>Pendiente</Th>
              <Th>Estado</Th>
              <Th>Acciones</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(product => {
              const qty = product.fastmax_quantity ?? product.quantity ?? 0;
              const isOut = qty === 0;
              const isLow = product.is_low_stock && !isOut;

              return (
                <tr
                  key={product.product_id}
                  className={isOut ? 'bg-red-50/40' : isLow ? 'bg-amber-50/40' : 'hover:bg-gray-50/60'}
                >
                  <td className="px-4 py-3">
                    <ProductIdentity product={product} />
                  </td>
                  <Td muted>{product.seller_name || '-'}</Td>
                  <td className="px-4 py-3">
                    <Sku value={product.sku} />
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-montserrat text-sm text-gray-600">
                      S/ {Number(product.price || 0).toFixed(2)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-montserrat font-extrabold text-lg ${
                      isOut ? 'text-red-600' : isLow ? 'text-amber-600' : 'text-gray-800'
                    }`}>
                      {qty}
                    </span>
                    <span className="font-montserrat text-xs text-gray-400 ml-1">uds</span>
                  </td>
                  <td className="px-4 py-3">
                    {product.pending_quantity > 0 ? (
                      <span className="font-montserrat text-xs font-bold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full">
                        {product.pending_quantity} uds
                      </span>
                    ) : (
                      <span className="font-montserrat text-xs text-gray-300">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {isOut ? (
                      <span className="flex items-center gap-1 text-xs font-montserrat font-bold text-red-600 bg-red-100 px-2.5 py-1 rounded-full w-fit">
                        <AlertCircle size={11} />
                        Sin stock
                      </span>
                    ) : isLow ? (
                      <span className="flex items-center gap-1 text-xs font-montserrat font-bold text-amber-600 bg-amber-100 px-2.5 py-1 rounded-full w-fit">
                        <TrendingDown size={11} />
                        Stock bajo
                      </span>
                    ) : (
                      <span className="text-xs font-montserrat text-green-600 bg-green-50 px-2.5 py-1 rounded-full w-fit block">
                        Saludable
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <IconButton onClick={() => onEdit(product)} title="Editar stock">
                        <Pencil size={14} />
                      </IconButton>
                      <IconButton onClick={() => onHistory(product)} title="Historial">
                        <History size={14} />
                      </IconButton>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ModalShell({ children, onClose, maxWidth }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className={`bg-white rounded-2xl shadow-xl w-full ${maxWidth} mx-4 overflow-hidden`}
        onClick={event => event.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function ActionButton({ onClick, disabled, icon, label, tone }) {
  const toneClass = {
    green: 'bg-green-600 hover:bg-green-700 text-white',
    amber: 'bg-amber-500 hover:bg-amber-600 text-white',
    red: 'bg-red-600 hover:bg-red-700 text-white',
  }[tone];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-montserrat font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${toneClass}`}
    >
      {icon}
      {label}
    </button>
  );
}

function IconButton({ children, onClick, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="p-2 rounded-lg border border-gray-200 text-gray-400 hover:text-brand-primary hover:border-brand-primary transition-colors"
    >
      {children}
    </button>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="bg-gray-50 border border-gray-100 rounded-xl p-3">
      <p className="text-xs font-montserrat text-gray-400">{label}</p>
      <p className="text-xl font-montserrat font-extrabold text-gray-800">{value}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const label = {
    pending: 'Pendiente',
    accepted: 'Aceptada',
    partially_accepted: 'Parcial',
    rejected: 'Rechazada',
  }[status] || status;

  const klass = {
    pending: 'bg-blue-50 text-blue-600',
    accepted: 'bg-green-50 text-green-600',
    partially_accepted: 'bg-amber-50 text-amber-600',
    rejected: 'bg-red-50 text-red-600',
  }[status] || 'bg-gray-50 text-gray-500';

  return (
    <span className={`text-xs font-montserrat font-bold px-2.5 py-1 rounded-full w-fit block ${klass}`}>
      {label}
    </span>
  );
}

function Sku({ value }) {
  return (
    <span className="font-montserrat text-xs text-gray-400 font-mono bg-gray-100 px-2 py-0.5 rounded">
      {value || '-'}
    </span>
  );
}

function Th({ children }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-montserrat font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">
      {children}
    </th>
  );
}

function Td({ children, strong = false, muted = false }) {
  return (
    <td className="px-4 py-3">
      <span className={`font-montserrat text-sm ${strong ? 'font-semibold text-gray-800' : muted ? 'text-gray-500' : 'text-gray-700'}`}>
        {children}
      </span>
    </td>
  );
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function historyTitle(item) {
  if (item.type === 'reception_request') {
    return `Solicitud de ${item.requested_quantity} uds${item.accepted_quantity ? `, aceptadas ${item.accepted_quantity}` : ''}`;
  }
  if (item.transaction_type === 'RECEPTION_ACCEPTED') {
    return `Admin acepto ${item.change_qty} uds`;
  }
  if (item.transaction_type === 'RECEPTION_PARTIAL_ACCEPTED') {
    return `Admin acepto parcialmente ${item.change_qty} uds`;
  }
  if (item.transaction_type === 'ADMIN_ADJUSTMENT') {
    return `Ajuste manual de ${item.change_qty > 0 ? '+' : ''}${item.change_qty} uds`;
  }
  return `${item.transaction_type || 'Movimiento'} ${item.change_qty > 0 ? '+' : ''}${item.change_qty || ''}`;
}
